import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import ImageUploader from './post/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUTOSAVE_INTERVAL = 30000; // 30 seconds

// Helper function to format time since last save
const formatTimeSince = (date) => {
  const seconds = Math.floor((new Date() - date) / 1000);
  
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds} seconds ago`;
  
  const minutes = Math.floor(seconds / 60);
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  
  return 'a while ago';
};

const CreatePostModal = ({ isOpen, onClose, onPostCreated, editPost = null }) => {
  const [activeTab, setActiveTab] = useState(editPost?.post_type || 'Reflection');
  const [title, setTitle] = useState(editPost?.title || '');
  const [body, setBody] = useState(editPost?.body || '');
  const [tags, setTags] = useState(editPost?.tags?.join(', ') || '');
  const [images, setImages] = useState(editPost?.images || []);  // NEW: Images state
  const [uploading, setUploading] = useState(false);  // NEW: Upload state
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const autoSaveTimerRef = useRef(null);
  const lastContentRef = useRef({ title: '', body: '', tags: '' });
  const isEditMode = editPost !== null;
  const isDraftEdit = isEditMode && editPost?.is_draft;

  const postTypes = ['Reflection', 'Question', 'Experience', 'Advice'];

  // Track changes to prompt save on close
  const handleInputChange = (setter, value) => {
    setter(value);
    setHasChanges(true);
  };

  const resetForm = () => {
    if (editPost) {
      // When editing, reset to original post values
      setTitle(editPost.title || '');
      setBody(editPost.body || '');
      setTags(editPost.tags?.join(', ') || '');
      setActiveTab(editPost.post_type || 'Reflection');
      setImages(editPost.images || []);
    } else {
      // When creating new, reset to empty
      setTitle('');
      setBody('');
      setTags('');
      setActiveTab('Reflection');
      setImages([]);
    }
    setHasChanges(false);
    setLastSaved(null);
    lastContentRef.current = { title: '', body: '', tags: '' };
  };

  // Image upload handlers

  const autoSaveDraft = useCallback(async () => {
    // Don't auto-save if there's no content or if already saving
    if (isSaving || loading) return;
    
    const currentContent = { title, body, tags };
    const hasContentChanged = 
      currentContent.title !== lastContentRef.current.title ||
      currentContent.body !== lastContentRef.current.body ||
      currentContent.tags !== lastContentRef.current.tags;

    // Only auto-save if there's actual content and it has changed
    if (hasContentChanged && (title.trim() || body.trim())) {
      setIsSaving(true);
      try {
        const tagsArray = tags.split(',').map(tag => tag.trim()).filter(tag => tag);
        
        await axios.post(`${API}/posts`, {
          title: title.trim() || 'Untitled Draft',
          body: body.trim() || '',
          post_type: activeTab,
          tags: tagsArray,
          is_draft: true
        });

        setLastSaved(new Date());
        lastContentRef.current = currentContent;
        
        // Silent auto-save (no toast notification to avoid interrupting user)
      } catch (error) {
        
        // Silently fail - don't interrupt user's typing
      } finally {
        setIsSaving(false);
      }
    }
  }, [isSaving, loading, title, body, tags, activeTab, API, axios]); // Added API and axios

  // Auto-save effect
  useEffect(() => {
    if (!isOpen) return;

    // Start auto-save timer
    autoSaveTimerRef.current = setInterval(() => {
      autoSaveDraft();
    }, AUTOSAVE_INTERVAL);

    // Cleanup on unmount or modal close
    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [isOpen, autoSaveDraft]);

  // Update time display every 10 seconds
  useEffect(() => {
    if (!isOpen || !lastSaved) return;

    const updateTimer = setInterval(() => {
      // Force re-render to update "X seconds ago" text
      setLastSaved(new Date(lastSaved));
    }, 10000);

    return () => clearInterval(updateTimer);
  }, [isOpen, lastSaved]);

  const handleClose = async () => {
    // If user has made changes, ask if they want to save as draft
    if (hasChanges && (title.trim() || body.trim())) {
      const shouldSave = window.confirm(
        'You have unsaved changes. Would you like to save this post as a draft?'
      );
      
      if (shouldSave) {
        await saveDraft();
      } else {
        resetForm();
        onClose();
      }
    } else {
      resetForm();
      onClose();
    }
  };

  const saveDraft = async () => {
    // Save post as draft even if incomplete
    setLoading(true);
    try {
      const tagsArray = tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      
      await axios.post(`${API}/posts`, {
        title: title.trim() || 'Untitled Draft',
        body: body.trim() || '',
        post_type: activeTab,
        tags: tagsArray,
        is_draft: true
      });

      toast({
        title: 'Draft saved',
        description: 'You can continue editing later from your profile.',
      });

      resetForm();
      onClose();
    } catch (error) {
      toast({
        title: 'Failed to save draft',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (isDraft = false) => {
    if (!title.trim() || !body.trim()) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in both title and body',
        variant: 'destructive',
      });
      return;
    }

    if (title.length > 300) {
      toast({
        title: 'Title too long',
        description: 'Title must be 300 characters or less',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const tagsArray = tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      
      const postData = {
        title: title.trim(),
        body: body.trim(),
        post_type: activeTab,
        tags: tagsArray,
        images: images,  // Include images
        is_draft: isDraft
      };

      let response;
      if (isEditMode) {
        // Update existing post
        response = await axios.patch(`${API}/posts/${editPost.id}`, postData);
      } else {
        // Create new post
        response = await axios.post(`${API}/posts`, postData);
      }

      toast({
        title: isEditMode 
          ? (isDraft ? 'Draft updated' : 'Post updated!') 
          : (isDraft ? 'Draft saved' : 'Post created!'),
        description: isEditMode 
          ? (isDraft ? 'Your draft has been saved.' : 'Your updated post will be reviewed by the Iniraa team.')
          : (isDraft ? 'You can continue editing later from your profile.' : 'Your post will be reviewed by the Iniraa team.'),
      });

      // Reset form
      resetForm();
      
      if (onPostCreated) onPostCreated(response.data);
      onClose();
    } catch (error) {
      toast({
        title: isEditMode ? 'Failed to update post' : 'Failed to create post',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" 
      data-testid="create-post-modal"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-[#7B3F9E]">
            {isEditMode ? (isDraftEdit ? 'Edit Draft' : 'Edit Post') : 'Create a Post'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 transition"
            data-testid="close-modal-button"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {postTypes.map((type) => (
            <button
              key={type}
              onClick={() => setActiveTab(type)}
              className={`px-4 py-3 font-medium transition ${
                activeTab === type
                  ? 'text-[#FF8C00] border-b-2 border-[#FF8C00]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`post-type-tab-${type.toLowerCase()}`}
            >
              {type}
            </button>
          ))}
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title
              <span className="text-gray-400 ml-2">
                ({title.length}/300)
              </span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => handleInputChange(setTitle, e.target.value)}
              maxLength={300}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none transition"
              placeholder="Enter your post title..."
              data-testid="post-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Body
              {/* FEATURE 4: Character counter for 5000 limit */}
              <span className="text-gray-400 ml-2">
                ({body.length}/5000)
              </span>
            </label>
            <textarea
              value={body}
              onChange={(e) => handleInputChange(setBody, e.target.value)}
              maxLength={5000}
              rows={8}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none transition resize-none"
              placeholder="Share your thoughts..."
              data-testid="post-body-input"
            />
            {/* Auto-save indicator */}
            {lastSaved && (
              <p className="text-xs text-gray-500 mt-2 flex items-center">
                {isSaving ? (
                  <>
                    <span className="inline-block w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2"></span>
                    Saving draft...
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3 mr-1 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Last saved {formatTimeSince(lastSaved)}
                  </>
                )}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => handleInputChange(setTags, e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none transition"
              placeholder="e.g., pelvic health, exercise, support"
              data-testid="post-tags-input"
            />
          </div>

          {/* Image Upload Section */}
          <ImageUploader
            images={images}
            setImages={setImages}
            uploading={uploading}
            setUploading={setUploading}
            onHasChanges={setHasChanges}
          />

          <div className="bg-[#FFF4E6] p-4 rounded-lg border border-[#FFE0B3]">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> All posts will be reviewed by the Iniraa team before being marked as verified.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              disabled={loading}
            >
              Cancel
            </button>
            
            {/* Show "Save as Draft" only for new posts or draft edits */}
            {(!isEditMode || isDraftEdit) && (
              <button
                type="button"
                onClick={() => handleSubmit(true)}
                disabled={loading}
                className="px-6 py-2 border-2 border-[#FF8C00] text-[#FF8C00] rounded-lg hover:bg-[#FFF4E6] transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="post-draft-button"
              >
                {loading ? 'Saving...' : (isEditMode ? 'Save Draft' : 'Save as Draft')}
              </button>
            )}
            
            {/* Main action button - "Publish" for drafts, "Post/Update" for others */}
            <button
              onClick={() => handleSubmit(false)}
              disabled={loading}
              className="px-6 py-2 bg-[#FF8C00] text-white rounded-lg hover:bg-[#E67E00] transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="post-submit-button"
            >
              {loading ? (isEditMode ? 'Updating...' : 'Posting...') : 
                (isDraftEdit ? 'Publish' : (isEditMode ? 'Update Post' : 'Post'))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePostModal;
